import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  type MapIslandDto,
  type IncomingAttackDto,
  type IncomingPlayerRaidDto,
} from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { DangerBadge } from '@/components/DangerBadge'
import { ReconComposition, ReconRepel, ReconLocked } from '@/components/ReconIntel'
import { scheduleBucketLabel } from '@/lib/recon'
import { screenConstant } from '@/components/game/map/geometry'
import { PennantIcon, ChevronRight } from '@/components/game/GameIcons'
import { MISSION_COLOR } from '@/components/game/map/mapMarkers'

/** Incoming-attack line colour, keyed by hostile faction (locked design). */
export function attackColor(faction: IncomingAttackDto['faction']): string {
  return faction.key === 'marine' ? '#6CA8FF' : '#B0143C' // Marine bleu roi · Pirate cramoisi
}

/**
 * Rose des vents (8 points, en français) de `from` vers `to`. Sert à dire d'où
 * surgit une attaque : son origine est un point d'HORIZON sur le bord de carte
 * (cf. `RaidOrigin` côté serveur — « indépendant des îles », l'ennemi surgit du
 * large), pas un repaire nommé ; on en donne donc la direction. Repère écran :
 * +x = est, +y = sud (y vers le bas).
 */
export function cardinalDirection(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const deg = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI
  const dirs = ['est', 'sud-est', 'sud', 'sud-ouest', 'ouest', 'nord-ouest', 'nord', 'nord-est']
  return dirs[((Math.round(deg / 45) % 8) + 8) % 8]
}

/**
 * Pulsing SVG line from the attack's origin tile to the player's island, carrying
 * the faction emblem. The pennant rides the line ∝ the remaining travel time: at
 * departure it sits on `origin`, at arrival on the island, lerped by
 * `(now − startedAt) / (eta − startedAt)`. Colour = faction (Marine bleu roi /
 * Pirate cramoisi). Exported so it can be mounted in isolation under a parent <svg>.
 */
export function IncomingRaidLayer({
  attack,
  myIsland,
  k = 1,
  onOpen,
}: {
  attack: IncomingAttackDto
  myIsland: MapIslandDto
  /** Scale courant de la couche transform (contre-scale la zone de clic). */
  k?: number
  /** Clic sur le tracé → ouvre le panneau de l'île (détail complet de l'attaque). */
  onOpen?: () => void
}) {
  const { origin } = attack

  // Tick once a second so the pennant visibly advances along the line.
  const [, setNow] = useState(Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const startMs = attack.startedAt ? new Date(attack.startedAt).getTime() : NaN
  const etaMs = new Date(attack.eta).getTime()
  const progress = Number.isFinite(startMs) && etaMs > startMs
    ? Math.max(0, Math.min(1, (Date.now() - startMs) / (etaMs - startMs)))
    : 0 // pas de startedAt (raid legacy) → pavillon à l'origine
  const px = origin.x + (myIsland.x - origin.x) * progress
  const py = origin.y + (myIsland.y - origin.y) * progress

  const ALERT = attackColor(attack.faction)
  return (
    <g
      data-role="incoming-raid"
      style={{ pointerEvents: onOpen ? 'stroke' : 'none', cursor: onOpen ? 'pointer' : undefined }}
      onClick={onOpen}
    >
      {/* Bande de clic transparente, plus large que le trait visible : on clique
          le tracé d'attaque comme un tracé de mission (cf. RouteLayer) pour ouvrir
          le détail complet dans le panneau. */}
      {onOpen && (
        <line
          data-role="incoming-raid-hit"
          aria-label={`Attaque en approche : ${attack.faction.label}`}
          x1={origin.x}
          y1={origin.y}
          x2={myIsland.x}
          y2={myIsland.y}
          stroke="transparent"
          strokeWidth={screenConstant(3, k)}
          strokeLinecap="round"
        />
      )}
      <line
        x1={origin.x}
        y1={origin.y}
        x2={myIsland.x}
        y2={myIsland.y}
        stroke={ALERT}
        strokeWidth="0.8"
        strokeDasharray="2 1.4"
      >
        <animate attributeName="opacity" values="0.25;0.95;0.25" dur="1.4s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" values="0;-3.4" dur="0.9s" repeatCount="indefinite" />
      </line>
      <text x={px} y={py} fontSize="5" textAnchor="middle" dominantBaseline="central" aria-hidden="true">
        {attack.faction.emoji}
        <animate attributeName="opacity" values="0.35;1;0.35" dur="1.4s" repeatCount="indefinite" />
      </text>
    </g>
  )
}

/**
 * Incoming PvP raids drawn as their own lines (one per raid) from each raid's
 * origin tile to the player's island, in the PvP-hostile red (`raid_pvp`, #EF4444)
 * — deliberately distinct from the NPC crimson (#B0143C) so the player tells a
 * human attacker from an NPC one at a glance. The pennant rides each line ∝ the
 * remaining travel time, exactly like {@link IncomingRaidLayer}. Exported so it
 * can be mounted in isolation under a parent <svg>.
 */
export function IncomingPvpRaidLayer({
  raids,
  myIsland,
  k = 1,
  onOpen,
}: {
  raids: IncomingPlayerRaidDto[]
  myIsland: MapIslandDto
  /** Scale courant de la couche transform (contre-scale la zone de clic). */
  k?: number
  /** Clic sur un tracé → ouvre le panneau de l'île (détail des raids en approche). */
  onOpen?: () => void
}) {
  // Tick once a second so the pennants visibly advance along the lines.
  const [, setNow] = useState(Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const ALERT = MISSION_COLOR.raid_pvp
  return (
    <>
      {raids.map((raid, i) => {
        const { origin } = raid
        // Precise ETA → advance the pennant; fuzzy schedule (no scheduledAt) →
        // pennant parked at the origin (we don't know the exact arrival).
        const etaMs = raid.scheduledAt ? new Date(raid.scheduledAt).getTime() : NaN
        // The launch instant isn't sent; approximate the trip start so the pennant
        // sits part-way (we lerp from "1h before ETA" — a stable visual cue, never
        // leaking the precise timing the Watchtower hasn't revealed).
        const startMs = Number.isFinite(etaMs) ? etaMs - 3_600_000 : NaN
        const progress =
          Number.isFinite(startMs) && etaMs > startMs
            ? Math.max(0, Math.min(1, (Date.now() - startMs) / (etaMs - startMs)))
            : 0
        const px = origin.x + (myIsland.x - origin.x) * progress
        const py = origin.y + (myIsland.y - origin.y) * progress
        return (
          <g
            key={`pvp-in-${raid.attackerPseudo ?? 'inconnu'}-${origin.x}-${origin.y}-${i}`}
            data-role="incoming-pvp-raid"
            style={{ pointerEvents: onOpen ? 'stroke' : 'none', cursor: onOpen ? 'pointer' : undefined }}
            onClick={onOpen}
          >
            {/* Bande de clic transparente (cf. IncomingRaidLayer / RouteLayer). */}
            {onOpen && (
              <line
                data-role="incoming-pvp-raid-hit"
                aria-label={`Raid en approche : ${raid.attackerPseudo ?? 'capitaine inconnu'}`}
                x1={origin.x}
                y1={origin.y}
                x2={myIsland.x}
                y2={myIsland.y}
                stroke="transparent"
                strokeWidth={screenConstant(3, k)}
                strokeLinecap="round"
              />
            )}
            <line
              x1={origin.x}
              y1={origin.y}
              x2={myIsland.x}
              y2={myIsland.y}
              stroke={ALERT}
              strokeWidth="0.8"
              strokeDasharray="2 1.4"
            >
              <animate attributeName="opacity" values="0.25;0.95;0.25" dur="1.4s" repeatCount="indefinite" />
              <animate attributeName="stroke-dashoffset" values="0;-3.4" dur="0.9s" repeatCount="indefinite" />
            </line>
            <text x={px} y={py} fontSize="5" textAnchor="middle" dominantBaseline="central" aria-hidden="true">
              🏴‍☠️
              <animate attributeName="opacity" values="0.35;1;0.35" dur="1.4s" repeatCount="indefinite" />
            </text>
          </g>
        )
      })}
    </>
  )
}

/**
 * Live timing + danger magnitude for an incoming attack (mirrors DefensePanel's
 * NextRaid). The Watchtower gates both: a precise ETA countdown is shown only when
 * `schedule.precise`, else a fuzzy timing bucket; the danger skull-badge only when
 * the magnitude is revealed, else a "force inconnue" locked hint.
 */
export function IncomingEta({ attack }: { attack: IncomingAttackDto }) {
  const { eta, danger, schedule } = attack
  const remaining = useCountdown(schedule.precise ? eta : null)
  return (
    <p className="mt-1 flex items-center justify-between gap-2 text-ocean-200">
      <span className="inline-flex items-center gap-1.5">
        {schedule.precise ? (
          <>
            <span className="text-ocean-300">Arrivée dans</span>
            <span className="font-display tabular-nums text-sand-100">{remaining}</span>
          </>
        ) : (
          <>
            <span className="text-ocean-300">Arrivée</span>
            <span className="font-display text-sand-200">{scheduleBucketLabel(schedule.bucket)}</span>
          </>
        )}
      </span>
      {danger !== null && danger !== undefined ? (
        <DangerBadge danger={danger} />
      ) : (
        <ReconLocked compact label="Force inconnue" />
      )}
    </p>
  )
}

/**
 * One incoming PvP raid readout in the map side-panel — the human-attacker twin
 * of the NPC `IncomingEta` + recon block. A glanceable TEASER, not a control
 * panel: it deliberately NEVER shows the precise ETA countdown (that stays
 * Watchtower-gated on the Mon île defence page, where the player upgrades the
 * tower). The whole block is a link to that gated block (`/island#incoming-pvp`).
 *
 * Floor = the attacker pseudo + the coarse urgency tier (Imminent/Bientôt/
 * Lointain), identical to the defence page floor; danger, composition and repel%
 * unlock tier by tier (locked nudges otherwise). Coloured with the PvP red.
 */
export function IncomingPvpRaidReadout({ raid }: { raid: IncomingPlayerRaidDto }) {
  const { attackerPseudo, danger, composition, repelEstimate, schedule, moreIntelIfUpgraded, intelTiers } = raid
  const c = MISSION_COLOR.raid_pvp
  return (
    <Link
      to="/island#incoming-pvp"
      className="block rounded-xl p-3 text-sm text-left space-y-2 transition-[filter] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rum-500/70"
      style={{ background: `${c}1a`, border: `1px solid ${c}66` }}
    >
      <p className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5" style={{ color: c }}>
          <PennantIcon className="w-4 h-4" />
          <strong>{attackerPseudo ?? 'Capitaine inconnu'}</strong> en approche
        </span>
        {danger !== null && danger !== undefined ? (
          <DangerBadge danger={danger} />
        ) : (
          <ReconLocked compact label="Force inconnue" />
        )}
      </p>
      {/* Timing on the MAP is the coarse urgency tier only — never the precise
          countdown. When the Watchtower has revealed the exact ETA
          (`schedule.precise`), the bucket collapses to 'precise' and carries no
          tier, so we show nothing here; the live timer lives on Mon île. */}
      {!schedule.precise && (
        <p className="flex items-center gap-1.5 text-ocean-200">
          <span className="text-ocean-300">Arrivée</span>
          <span className="font-display text-sand-200">{scheduleBucketLabel(schedule.bucket)}</span>
        </p>
      )}
      <ReconComposition
        composition={composition}
        showLockedHint={moreIntelIfUpgraded}
        requiredLevel={intelTiers?.composition}
      />
      <ReconRepel estimate={repelEstimate} showLockedHint={moreIntelIfUpgraded} requiredLevel={intelTiers?.repel} />
      <p className="flex items-center justify-end gap-1 text-xs text-ocean-300">
        Détail sur Mon île <ChevronRight className="w-3.5 h-3.5" />
      </p>
    </Link>
  )
}
